(ns dre.components.timeline
  (:require
   [applied-science.js-interop :as j]
   ["d3" :as d3]
   [dre.page-util :refer [short-num]]
   [dre.utils.bem :as bem]
   [reagent.core :as r]
   [goog.dom :as gdom]
   ["react-sizeme" :as react-sizeme]))

(def component-name :timeline)

(defn date->year
  [d]
  (if (instance? js/Date d)
    (js/parseInt (.getUTCFullYear d) 10)
    (js/parseInt d 10)))

(defn utc-date
  ([x]
   (js/Date. (.UTC js/Date x)))
  ([x y]
   (js/Date. (.UTC js/Date x y)))
  ([x y z]
   (js/Date. (.UTC js/Date x y z)))
  ([x y z q]
   (js/Date. (.UTC js/Date x y z q))))

(defn barchart
  [element data extent-change extent-selection w h]
  (let [d3-el (j/call d3 :select element)
        svg (.. (j/call d3-el :append "svg")
                (attr "fill" "#d046ff"))
        margin {:bottom 20 :left 40 :top 0 :right 25}
        h (- h (+ (:top margin) (:bottom margin)))
        w (- w (+ (:left margin) (:right margin)))

        data (map (fn [e] (assoc e :date (utc-date (:year e)))) data)
        js-data (clj->js data)
        x-extent (.extent d3 js-data (fn [d] (j/get d :date)))
        int-years (map date->year x-extent)
        ordinal-domain (into-array (concat (map #(utc-date (str %))
                                                (range
                                                 (first int-years)
                                                 (last int-years)))
                                           (if (= (first int-years) (last int-years))
                                             [(utc-date (last int-years) 5 1)]
                                             [(utc-date (last int-years) 11 30)])))

        data (map (fn [e] (assoc e :date (if (= (js/parseInt (:year e)) (last int-years))
                                           (utc-date (:year e) 11 30)
                                           (utc-date (:year e))))) data)
        js-data (clj->js data)
        x-extent (.extent d3 ordinal-domain (fn [d] d))

        y-scale (.. (.scaleLinear d3)
                    (range (into-array [h 0]))
                    (domain (into-array [0 (.max d3 js-data (fn [d] (j/get d :count)))])))
        x-scale-time (.scaleTime d3)
        x-scale-format (..
                        x-scale-time
                        (tickFormat 0 "%Y"))
        x-scale (.. x-scale-time
                    (range (into-array [0 w]))
                    (domain x-extent))

        x-scale-ordinal (.. (.scaleBand d3)
                            (range (into-array [0 w]))
                            (domain ordinal-domain))
        g (.. svg
              (append "g")
              (attr "transform" (str "translate(" (:left margin) "," (:top margin) ")")))
        bar (.. g
                (append "g"))
        bar (.. bar
                (selectAll "g")
                (data js-data)
                enter)
        x-axis (.. (j/call d3 :axisBottom x-scale)
                   (tickSizeOuter 0)
                   (tickFormat x-scale-format)
                   (ticks 4))

        y-axis (.. (j/call d3 :axisLeft y-scale)
                   (tickFormat (fn [d] (short-num d))))

        on-brush (fn [extent-change event d]
                   (let [selection (j/get event :selection)
                         left (j/call x-scale :invert (first selection))
                         right (j/call x-scale :invert (second selection))]
                     (extent-change (if (= left right)
                                      nil
                                      [left right]))))

        brush (.. (j/call d3 :brushX)
                  (extent (into-array [(into-array [0 0]) (into-array [w h])])))
        brushg (.. g
                   (append "g")
                   (attr "class" "brush")
                   (call brush))
        _ (if extent-selection
            (do
              (.. (j/call d3 :select (str "#timeline-tooltip-s"))
                  (style "top" (str (+ 0  10) "px"))
                  (style "left" (str (+ 0 (max 0 (x-scale (first extent-selection)))) "px"))
                  (style "opacity" 1.0)
                  (select ".tooltip-inner")
                  (text (fn [_] (date->year (j/call x-scale :invert (max 0 (x-scale (first extent-selection))))))))
              (.. (j/call d3 :select (str "#timeline-tooltip-e"))
                  (style "top" (str (+ 0  10) "px"))
                  (style "left" (str (+ 40 (min w (x-scale (second extent-selection)))) "px"))
                  (style "opacity" 1.0)
                  (select ".tooltip-inner")
                  (text (fn [_] (date->year (j/call x-scale :invert (min w (x-scale (second extent-selection))))))))
              (.. brushg
                  (call (j/get brush :move)  (into-array [(max 0 (x-scale (first extent-selection)))
                                                          (min w (x-scale (second extent-selection)))]))))
            (do
              (.. (j/call d3 :select (str "#timeline-tooltip-s"))
                  (style "opacity" 0))
              (.. (j/call d3 :select (str "#timeline-tooltip-e"))
                  (style "opacity" 0))))
        _ (.. brush
              (on "end" (partial on-brush extent-change))
              (on "brush" (fn [e]
                            (let [es (j/get e :selection)]
                              (.. (j/call d3 :select (str "#timeline-tooltip"))
                                  (style "opacity" 0))
                              (.. (j/call d3 :select (str "#timeline-tooltip-s"))
                                  (style "top" (str (+ 0  10) "px"))
                                  (style "left" (str (+ 0 (first es)) "px"))
                                  (style "opacity" 1.0)
                                  (select ".tooltip-inner")
                                  (text (fn [_] (date->year (j/call x-scale :invert (first es))))))
                              (.. (j/call d3 :select (str "#timeline-tooltip-e"))
                                  (style "top" (str (+ 0  10) "px"))
                                  (style "left" (str (+ 40 (second es)) "px"))
                                  (style "opacity" 1.0)
                                  (select ".tooltip-inner")
                                  (text (fn [_] (date->year (j/call x-scale :invert (second es))))))))))]
    (.. g
        (append "g")
        (attr "transform" (str "translate(" 0 "," h ")"))
        (attr "class" "x-axis")
        (call x-axis))

    (.. g
        (append "g")
        (attr "class" "y-axis")
        (call y-axis))

    (.. bar
        (append "rect")
        (attr "data-active" true)
        (attr "x" (fn [d] (x-scale-ordinal (j/get d :date))))
        (attr "width" (fn [_] (j/call x-scale-ordinal :bandwidth)))
        (attr "y" (fn [d] 
                    (y-scale (j/get d :count))))
        (attr "height" (fn [d] (- h (y-scale (j/get d :count))))))
    (.. brushg
        (selectAll "rect")
        (attr "height" h)
        (on "mouseout" (fn [e d]
                         (.. (j/call d3 :select (str "#timeline-tooltip"))
                             (style "opacity" 0))))
        (on "mousemove" (fn [e d]
                          (let [dy -40
                                dx 40
                                pointer (j/call d3 :pointer e)] 
                            (.. (j/call d3 :select (str "#timeline-tooltip"))
                                (style "top" (str (+ dy (last pointer)) "px"))
                                (style "left" (str (+ dx (first pointer)) "px"))
                                (style "opacity" 1.0)
                                (select ".tooltip-inner")
                                (text (fn [_] (date->year (j/call x-scale :invert (first pointer)))))))
                          )))))

(defn timeline*
  [data extent-change extent-selection width height]
  (let [element (atom nil)
        destroy!
        (fn [_this]
          (when @element
            (gdom/removeChildren @element)))
        repaint!
        (fn [this data extent-change extent-selection width height]
          (destroy! this)
          (barchart @element data extent-change extent-selection width height))]
    (r/create-class
     {:component-did-mount
      (fn [this]
        (repaint! this data extent-change extent-selection width height))
      :component-will-unmount
      (fn [this]
        (destroy! this))
      :component-will-update
      (fn [this [_ data extent-change extent-selection width height]]
        (repaint! this data extent-change extent-selection width height))
      :reagent-render
      (fn [data extent-change extent-selection width height]
        [:div
         {:style
          {:width "100%"
           :height "100%"}}
         [:div
          {:ref #(reset! element %)
           :style
           {:width "100%"
            :height "100%"}}]
         [:div.tooltip.bs
          {:id :timeline-tooltip}
          [:div.tooltip-inner]]
         [:div.tooltip.bs
          {:id :timeline-tooltip-s}
          [:div.tooltip-inner]]
         [:div.tooltip.bs
          {:id :timeline-tooltip-e}
          [:div.tooltip-inner]]])})))

(defn timeline
  [data extent-change extent-selection]
  [:div {:class component-name}
   [(r/adapt-react-class react-sizeme/SizeMe)
    {:monitorWidth true :monitorHeight true}
    (fn [props]
      (let [props (js->clj props :keywordize-keys true)]
        (r/as-element [timeline*
                       data
                       extent-change
                       extent-selection
                       (:width (:size props))
                       (:height (:size props))])))]])
